import React, { useState } from 'react';
import { X, Heart, UserCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export type SignupKind = 'volunteer' | 'attendee';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventTitle: string;
  kind: SignupKind;
  onSignedUp?: () => void;
}

const COPY: Record<SignupKind, {
  table: 'event_volunteers' | 'event_attendees';
  title: string;
  intro: string;
  success: string;
  duplicate: string;
  icon: typeof Heart;
  iconClass: string;
  buttonClass: string;
}> = {
  volunteer: {
    table: 'event_volunteers',
    title: 'Volunteer',
    intro: 'Thank you for your interest in volunteering for',
    success: 'Thank you for volunteering!',
    duplicate: 'You have already volunteered for this event',
    icon: Heart,
    iconClass: 'text-red-600',
    buttonClass: 'bg-red-600 hover:bg-red-700',
  },
  attendee: {
    table: 'event_attendees',
    title: 'Attend',
    intro: "We're glad you're planning to attend",
    success: "You're on the list. See you there!",
    duplicate: "You're already signed up to attend this event",
    icon: UserCheck,
    iconClass: 'text-emerald-600',
    buttonClass: 'bg-emerald-600 hover:bg-emerald-700',
  },
};

const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose, eventId, eventTitle, kind, onSignedUp }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ email: '', phone: '' });

  if (!isOpen) return null;

  const copy = COPY[kind];
  const Icon = copy.icon;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = { email: '', phone: '' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    if (newErrors.email || newErrors.phone) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from(copy.table).insert([{
        event_id: eventId,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
      }]);

      if (error) {
        if (error.code === '23505') {
          toast.error(copy.duplicate);
          return;
        }
        throw error;
      }

      toast.success(copy.success);
      setFormData({ name: '', email: '', phone: '' });
      onSignedUp?.();
      onClose();
    } catch (error) {
      console.error(`Error submitting ${kind} sign-up:`, error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone' && value.replace(/\D/g, '').length > 10) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasError ? 'border-red-500' : 'border-gray-300'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Icon className={`w-6 h-6 ${copy.iconClass}`} />
            <h2 className="text-2xl font-bold text-gray-900">{copy.title}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-600 mb-6">
            {copy.intro} <strong>{eventTitle}</strong>. Please provide your contact information below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor={`${kind}-name`} className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                id={`${kind}-name`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                maxLength={100}
                className={inputClass(false)}
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor={`${kind}-email`} className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input
                type="email"
                id={`${kind}-email`}
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                maxLength={255}
                className={inputClass(!!errors.email)}
                placeholder="john@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor={`${kind}-phone`} className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                id={`${kind}-phone`}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={inputClass(!!errors.phone)}
                placeholder="(615) 555-0123"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${copy.buttonClass}`}
              >
                <Icon className="w-4 h-4" />
                <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
